import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { FormattedDate, numberFormatLong } from "components/app_layout/Typography";
import { SavedEntityCardContainer } from "components/entities/utils";
import SuppliersContainer from "components/table_components/SuppliersContainer";
import { EmptyLogoImage } from "components/table_components/TableCellStyles";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import NoticeStage from "lib/core_components/NoticeStage";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useRecordViewer } from "lib/providers/RecordViewer";
import { BuyerActivityObjectType } from "lib/types/graphQLEnums";
import { capitalize } from "lib/utils";
import { CoreNotice, LotSupplier } from "./types";
import { getNoticeDateInfo } from "./utils";

type SavedNotice = CoreNotice & { isSaved?: boolean };

export default function RecommendedNotices({
  relatedNotices,
  savedNotices,
  isLoading,
  objectType,
  saveToButton,
}: {
  relatedNotices: CoreNotice[];
  savedNotices: SavedNotice[];
  isLoading: boolean;
  objectType: BuyerActivityObjectType;
  saveToButton?: JSX.Element;
}) {
  const { viewRecord } = useRecordViewer();

  const allNotices: SavedNotice[] = [
    ...relatedNotices,
    ...savedNotices.map((notice) => ({ ...notice, isSaved: true })),
  ].sort((a, b) => new Date(b.publishDate ?? 0).getTime() - new Date(a.publishDate ?? 0).getTime());

  if (isLoading) {
    return (
      <Flex column width="100%" gap={32}>
        <SkeletonText width="100%" fontSize={135} variant="rectangular" customSpacing />
        <SkeletonText width="100%" fontSize={135} variant="rectangular" customSpacing />
      </Flex>
    );
  }

  return (
    <Flex column gap={32} width={"100%"}>
      {allNotices.map((notice) => (
        <SavedEntityCardContainer
          key={notice.guid}
          //TODO: remove and eventData and replace with TrackingProvider when BuyerActivity is rendered in LeadDetailsPage etc
          onClick={() => viewRecord(notice.guid, { "Context source": capitalize(objectType) })}
          padding={15}
        >
          <RecommendedNoticeCard notice={notice} saveToButton={saveToButton} />
        </SavedEntityCardContainer>
      ))}
    </Flex>
  );
}

export function RecommendedNoticeCard({
  notice,
  saveToButton,
}: {
  notice: SavedNotice;
  saveToButton?: JSX.Element;
}) {
  const formattedValue = numberFormatLong(notice.value ?? null);

  const dateInfo = getNoticeDateInfo(notice);

  return (
    <Flex column gap={16} width="100%">
      <Flex justifyContent="space-between" alignItems="flex-start" gap={24}>
        <Flex gap={8}>
          {notice.stagev2 && <NoticeStage stage={notice.stagev2} />}
          {notice.isSaved && <SavedNoticeTag>Saved notice</SavedNoticeTag>}
        </Flex>
        {saveToButton}
      </Flex>
      <Text bold color="sysPrimaryDefault">
        {notice.name || "Untitled Notice"}
      </Text>
      <Flex gap={24}>
        <Flex gap={7} alignItems="center">
          <UIcon icon="calendar" color="sysTextSecondary" />
          <PropertyText color="sysTextSecondary">
            Published <FormattedDate date={notice.publishDate} placeholder="--" />
          </PropertyText>
        </Flex>
        <Flex gap={7} alignItems="center">
          <UIcon icon="hourglass01" color="sysTextSecondary" />
          <PropertyText color="sysTextSecondary">
            {dateInfo.label} <FormattedDate date={dateInfo.date} placeholder="--" />
          </PropertyText>
        </Flex>
        <Flex gap={7} alignItems="center">
          <UIcon icon="currencyPoundCircle" color="sysTextSecondary" />
          <PropertyText color="sysTextSecondary">{formattedValue}</PropertyText>
        </Flex>
        <SuppliersPopover notice={notice} />
      </Flex>
    </Flex>
  );
}

function SuppliersPopover({ notice }: { notice: CoreNotice }) {
  const suppliers = (notice.lots ?? []).reduce<LotSupplier[]>(
    (acc, lot) => acc.concat(lot.suppliers?.map((supplier) => supplier) || []),
    [],
  );

  if (suppliers.length === 0) {
    return null;
  }

  const mappedSuppliers = suppliers.map((s) => ({
    guid: s.guid,
    name: s.name,
  }));

  return (
    <SupplierWrapper alignItems="center" gap={8}>
      <EmptyLogoImage>
        <UIcon icon="trophy01" size={12} />
      </EmptyLogoImage>
      <SuppliersContainer recordName={notice.name ?? "--"} suppliers={mappedSuppliers} ellipsis />
    </SupplierWrapper>
  );
}

const PropertyText = styled(Text)({
  whiteSpace: "nowrap",
});

const SupplierWrapper = styled(Flex)({
  overflow: "hidden",
});

const SavedNoticeTag = styled(Text)(({ theme }) => ({
  backgroundColor: theme.colors.sysBackgroundAlternative,
  color: theme.colors.sysTextSecondary,
  padding: "2px 8px",
}));
